import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  cn,
  layout,
  spacing,
  typography,
  sizes
} from "@/utilities/tailwind-utils";

interface ManagementPageProps {
  title: string;
  description: string;
  icon: LucideIcon;
  isLoading?: boolean;
  error?: string | null;
  children: ReactNode;
}

export function ManagementPage({
  title,
  description,
  icon: Icon,
  isLoading,
  error,
  children
}: ManagementPageProps) {
  const header = (
    <CardHeader>
      <CardTitle className={cn(layout.flexCenterGap2)}>
        <Icon className={sizes.size5} />
        {title}
      </CardTitle>
      <CardDescription>{description}</CardDescription>
    </CardHeader>
  );

  if (isLoading) {
    return (
      <Card>
        {header}
        <CardContent>
          <div className={cn(layout.flexCenter, spacing.py16)}>
            <div className={cn(typography.textCenter)}>
              <div
                className={cn(
                  "h-8 bg-gray-200 rounded w-48 animate-pulse",
                  spacing.mb4
                )}
              />
              <div className="h-4 bg-gray-200 rounded w-64 animate-pulse" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        {header}
        <CardContent>
          <div className={cn(layout.flexCenter, spacing.py16)}>
            <div className={cn(typography.textCenter)}>
              <h2 className={cn(typography.text2xlSemibold, spacing.mb4)}>
                Error Loading Data
              </h2>
              <p className={cn(typography.textMuted, spacing.mb4)}>{error}</p>
              <Button onClick={() => window.location.reload()}>Retry</Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      {header}
      <CardContent>{children}</CardContent>
    </Card>
  );
}
